import { useCallback, useEffect, useState, useTransition } from "react";
import baseStyles from "../BaseInputs.module.css";

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  value: string | number | undefined;
  onNewValue?: (ret: string | number) => void;
  label?: string;
}

/**
 * Generic input used by the ObjectEditor for editing simple string fields.
 *
 * @param props - Destructured parameter object
 * @param props.id - Unique identifier used for the input element and its label.
 * @param props.value - Current value of the input. May be a `string`, `number`, or `undefined`.
 * @param props.onNewValue - Optional callback invoked on blur with the new input value (string or number).
 * @param props.label - Optional label text rendered above the input when provided.
 */
export const BaseInput = ({
  id,
  value,
  onNewValue,
  label,
  ...rest
}: BaseInputProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? "");
  useEffect(() => {
    startTransition(() => setInputValue(value?.toString() ?? ""));
  }, [value]);
  /**
   * Update the local input state when the user types. The updated value
   * will be propagated to the parent on blur via `onNewValue`.
   *
   * @param e - The change event from the input element.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  const handleInputBlur = useCallback(() => {
    /* istanbul ignore else */
    if (onNewValue) {
      const newValue = typeof value === "number" ? parseFloat(inputValue) : inputValue;
      onNewValue(newValue);
    }
  }, [onNewValue, inputValue, value]);

  return (
    <div className={baseStyles.holder}>
      {label !== undefined && (
        <label
          id={`${id}-label`}
          className={baseStyles.label}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <input
        {...rest}
        type={typeof value === "number" ? "number" : "text"}
        id={id}
        className={[baseStyles.baseInput, rest.className].filter((c) => c).join(" ")}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      />
    </div>
  );
};

BaseInput.displayName = "BaseInput";
